import { ApplicationConfig, importProvidersFrom, inject } from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  Router,
  provideRouter,
  withEnabledBlockingInitialNavigation,
  withHashLocation,
  withInMemoryScrolling,
  withRouterConfig,
  withViewTransitions,
} from '@angular/router';

import { HttpLink } from 'apollo-angular/http';
import { ApolloLink, InMemoryCache } from '@apollo/client/core';
import { setContext } from '@apollo/client/link/context';

import {
  DropdownModule,
  SidebarModule,
} from '@coreui/angular-pro';
import { IconSetService } from '@coreui/icons-angular';
import { routes } from './app.routes';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { authInterceptor } from '@infra-adapters/interceptors/auth.interceptor';
import {
  graphqlErrorInterceptor,
  httpAuthErrorInterceptor,
} from '@infra-adapters/interceptors/authError.interceptor';
import { provideApollo } from 'apollo-angular';
import { StorageKeys } from '@core-interfaces/global';
import { environment } from '@infra-env/environment';
import { ToastrService, provideToastr } from 'ngx-toastr';
import { successInterceptor } from '@infra-adapters/interceptors/success.interceptor';

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(
      routes,
      withRouterConfig({
        onSameUrlNavigation: 'reload',
      }),
      withInMemoryScrolling({
        scrollPositionRestoration: 'top',
        anchorScrolling: 'enabled',
      }),
      withEnabledBlockingInitialNavigation(),
      withViewTransitions(),
      withHashLocation()
    ),
    importProvidersFrom(SidebarModule, DropdownModule),
    IconSetService,
    provideAnimations(),
    provideToastr({ preventDuplicates: true, timeOut: 5000 }),
    provideHttpClient(
      withInterceptors([
        authInterceptor,
        httpAuthErrorInterceptor,
      ])
    ),
    provideApollo(() => {
      const httpLink = inject(HttpLink);

      const authLink = setContext((_, { headers }) => {
        const token = sessionStorage.getItem(StorageKeys.session_token);

        let temp = {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        };

        return {
          headers: {
            ...headers,
            ...temp,
          },
        };
      });

      const toastAlertService = inject(ToastrService);
      const router = inject(Router);
      const errorLink = graphqlErrorInterceptor(toastAlertService, router);
      const successLink = successInterceptor(toastAlertService);

      return {
        link: ApolloLink.from([
          errorLink,
          successLink,
          authLink,
          httpLink.create({ uri: environment.apiUrl }),
        ]),
        cache: new InMemoryCache({
          addTypename: false,
        }),
      };
    }),
  ],
};
